import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import StatusBadge from '../components/StatusBadge';
import Pagination from '../components/Pagination';

export default function TargetsScreen() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [platform, setPlatform] = useState('');
  const [status, setStatus] = useState('');
  const [tier, setTier] = useState('');
  const [platforms, setPlatforms] = useState<any[]>([]);

  const fetchTargets = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.getTargets({ page, limit: 50, platform, status, tier });
      setData(result);
    } finally {
      setLoading(false);
    }
  }, [page, platform, status, tier]);

  useEffect(() => {
    api.getPlatforms().then(r => setPlatforms(r.platforms || [])).catch(() => {});
  }, []);

  useEffect(() => { fetchTargets(); }, [fetchTargets]);

  const handleFilterChange = (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLSelectElement>) => {
    setter(e.target.value);
    setPage(1);
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', color: 'var(--gray-900)' }}>Targets</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--gray-500)', fontSize: '14px' }}>All scrape targets across platforms</p>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <select value={platform} onChange={handleFilterChange(setPlatform)} style={selectStyle}>
          <option value="">All Platforms</option>
          {platforms.map((p: any) => <option key={p.id} value={p.id}>{p.display_name} ({p.enabled_targets})</option>)}
        </select>
        <select value={status} onChange={handleFilterChange(setStatus)} style={selectStyle}>
          <option value="">All Status</option>
          <option value="succeeded">Succeeded</option>
          <option value="failed">Failed</option>
          <option value="idle">Idle</option>
          <option value="syncing">Syncing</option>
          <option value="queued">Queued</option>
        </select>
        <select value={tier} onChange={handleFilterChange(setTier)} style={selectStyle}>
          <option value="">All Tiers</option>
          <option value="T1">T1 (10 min)</option>
          <option value="T2">T2 (30 min)</option>
        </select>
      </div>

      {loading && !data ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : data ? (
        <div className="card">
          <Pagination page={data.pagination.page} pages={data.pagination.pages} total={data.pagination.total} onPageChange={setPage} />
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--gray-200)', textAlign: 'left' }}>
                  <th style={th}>Platform</th>
                  <th style={th}>Target</th>
                  <th style={th}>Tier</th>
                  <th style={th}>Status</th>
                  <th style={th}>Interval</th>
                  <th style={th}>Last Sync</th>
                  <th style={th}>Runtime</th>
                  <th style={th}>Error</th>
                </tr>
              </thead>
              <tbody>
                {data.targets.map((t: any) => (
                  <tr key={t.id} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                    <td style={td}>{t.platform_id}</td>
                    <td style={{ ...td, fontWeight: 500 }}>{t.display_name || t.slug}</td>
                    <td style={td}>{t.tier}</td>
                    <td style={td}><StatusBadge status={t.sync_status} /></td>
                    <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{t.sync_interval_minutes}m</td>
                    <td style={{ ...td, color: 'var(--gray-500)', fontSize: '12px' }}>
                      {t.last_sync_at ? new Date(t.last_sync_at).toLocaleString() : 'Never'}
                    </td>
                    <td style={{ ...td, fontVariantNumeric: 'tabular-nums', color: 'var(--gray-500)' }}>
                      {t.last_sync_duration_ms ? `${(t.last_sync_duration_ms / 1000).toFixed(1)}s` : '-'}
                    </td>
                    <td style={{ ...td, color: '#ef4444', fontFamily: 'monospace', fontSize: '11px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t.last_error || ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={data.pagination.page} pages={data.pagination.pages} total={data.pagination.total} onPageChange={setPage} />
        </div>
      ) : null}
    </>
  );
}

const th: React.CSSProperties = { padding: '8px 12px' };
const td: React.CSSProperties = { padding: '8px 12px' };
const selectStyle: React.CSSProperties = {
  padding: '8px 12px',
  borderRadius: '8px',
  border: '1px solid var(--gray-300)',
  fontSize: '13px',
  background: '#fff',
  cursor: 'pointer',
};
